/**
 * Skeleton loader for the Notifications page
 * Matches the exact structure of the real component with default state:
 * - Filters are HIDDEN by default (showFilters = false)
 * - Bulk actions are HIDDEN by default (selectedIds.size = 0)
 */
import { Card } from '@splitfiesta/ui';

import { SkeletonText, SkeletonTitle } from './SkeletonComponents';

export function NotificationsPageSkeleton() {
  return (
    <div
      className="max-w-2xl mx-auto"
      data-testid="notifications-page-skeleton"
    >
      {/* Simplified Header with Quick Actions - matches real component */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <SkeletonTitle className="w-48" />
            <SkeletonText className="w-32 mt-2" size="sm" />
          </div>

          {/* Quick Actions Skeleton */}
          <div className="flex gap-2">
            <div className="h-9 w-24 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
            <div className="h-9 w-28 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
          </div>
        </div>
      </div>

      {/* Filter panel is HIDDEN by default - showFilters starts as false */}
      {/* Bulk actions are HIDDEN by default - selectedIds.size starts as 0 */}

      {/* Notifications List Skeleton */}
      <Card>
        {/* Select All Checkbox Skeleton */}
        <div className="-mx-6 px-6 py-3 border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
          <div className="flex items-center gap-2">
            <div className="h-4 w-4 bg-gray-200 dark:bg-gray-700 rounded-sm animate-pulse" />
            <SkeletonText className="w-20" size="sm" />
          </div>
        </div>

        {/* Notification Items Skeleton */}
        <div>
          {[...Array(5)].map((_, index) => (
            <div
              key={index}
              className={`flex items-start gap-3 -mx-6 px-6 py-4 transition-colors ${
                index < 2
                  ? 'bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30'
                  : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'
              } ${
                index !== 4
                  ? 'border-b border-gray-200 dark:border-gray-700'
                  : ''
              }`}
            >
              {/* Checkbox Skeleton */}
              <div className="rounded-sm border-gray-300 dark:border-gray-600 h-4 w-4 bg-gray-200 dark:bg-gray-700 animate-pulse shrink-0 mt-3" />

              {/* Notification Content Skeleton */}
              <div className="flex-1 min-w-0">
                <div className="space-y-2">
                  {/* Title/Type */}
                  <SkeletonText className="w-32" size="sm" />
                  {/* Main content */}
                  <SkeletonText className="w-3/4" />
                  <SkeletonText className="w-5/6" />
                  {/* Metadata (time, group) */}
                  <div className="flex items-center gap-2 mt-2">
                    <SkeletonText className="w-24" size="sm" />
                    <span className="text-gray-400">•</span>
                    <SkeletonText className="w-20" size="sm" />
                  </div>
                </div>
              </div>

              {/* Unread indicator skeleton for first 2 items */}
              {index < 2 && (
                <div className="shrink-0 mt-5">
                  <div className="h-2 w-2 rounded-full bg-blue-500" />
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Load More Button Skeleton */}
        <div className="-mx-6 px-6 py-4 flex justify-center border-t border-gray-200 dark:border-gray-700">
          <SkeletonText className="w-24" />
        </div>
      </Card>
    </div>
  );
}
